// Adds background to captions in SVG string
export const addCaptionBackgrounds = (
	svgString: string,
	ratio: number
): string => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(svgString, "image/svg+xml");
	const svgElement = doc.documentElement;
	const captions = svgElement.querySelector("#captions");

	if (!captions) {
		return svgString;
	}

	const textElements = captions.querySelectorAll("text");
	textElements.forEach((element) => {
		const isActive = element.getAttribute("stroke");

		const fontSize = 4 * ratio;
		element.setAttribute("font-size", `${fontSize}mm`);

		if(isActive) {
			element.style.removeProperty("stroke");
			element.style.setProperty("fill", "white");
			element.setAttribute("stroke", "none");
		}

		// Create temporary SVG element to calculate the bounding box of the text element
		const tempSvg = document.createElementNS(
			"http://www.w3.org/2000/svg",
			"svg"
		);
		const tempText = element.cloneNode(true) as SVGTextElement;
		if(!tempText.getBBox) {
			return;
		}
		tempSvg.appendChild(tempText);
		document.body.appendChild(tempSvg);

		const bbox = tempText.getBBox();
		document.body.removeChild(tempSvg);

		const xPadding = 20 * ratio;
		const yPadding = 8 * ratio;
		const width = bbox.width + xPadding;
		const height = bbox.height + yPadding;
		const x = +element.getAttribute("x") - width / 2;
		const y = +element.getAttribute("y") - height / 2;

		// Create background rectangel for label
		const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
		rect.setAttribute("x", `${x}`);
		rect.setAttribute("y", `${y}`);
		rect.setAttribute("transform", element.getAttribute("transform"));
		rect.setAttribute("width", `${width}`);
		rect.setAttribute("height", `${height}`);
		rect.setAttribute("rx", `${2 * ratio}`);
		rect.setAttribute("stroke", "none");
		rect.setAttribute("fill", isActive ? "#00adef" : "white");

		captions.prepend(rect);
	});

	// Serialize the SVG element back to string
	const serializer = new XMLSerializer();
	return serializer.serializeToString(svgElement);
};

export const dottedGuideLines = (
	svgString: string
): string => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(svgString, "image/svg+xml");
	const svgElement = doc.documentElement;
	const guideLines = svgElement.querySelector("#guide-lines");
	if(guideLines === null) {
		return svgString;
	}
	const dotDash = 7;
	guideLines.setAttribute("stroke-dasharray", `${dotDash},${dotDash}`);
	const serializer = new XMLSerializer();
	return serializer.serializeToString(svgElement);
};

export const getTexturedSVG = (
	svgString: string,
	sideColor: string,
	sideAppearance: string,
	preserveAspectRatio?: string
): string => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(svgString, "image/svg+xml");
	const svgElement = doc.documentElement;
	const shapePath = svgElement.querySelector("#shape");

	if (preserveAspectRatio) {
		svgElement.setAttribute("preserveAspectRatio", preserveAspectRatio);
	}

	const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");

	// Add metallic texture
	if(sideAppearance === "metallic") {
		const metallicGradient = document.createElementNS(
			"http://www.w3.org/2000/svg",
			"linearGradient"
		);
		metallicGradient.setAttribute("id", "metallicGradient");
		metallicGradient.setAttribute("x1", "0");
		metallicGradient.setAttribute("x2", "0");
		metallicGradient.setAttribute("y1", "0");
		metallicGradient.setAttribute("y2", "1");
		metallicGradient.setAttribute("gradientTransform", "rotate(80, 0.5, 0.5)");
		metallicGradient.innerHTML = `
			<stop offset="0%" stop-color="white" stop-opacity="0.1" />
			<stop offset="10%" stop-color="white" stop-opacity="0.3" />
			<stop offset="30%" stop-color="white" stop-opacity="0.1" />
			<stop offset="40%" stop-color="black" stop-opacity="0.1" />
			<stop offset="50%" stop-color="black" stop-opacity="0.3" />
			<stop offset="60%" stop-color="black" stop-opacity="0.1" />
			<stop offset="70%" stop-color="white" stop-opacity="0.1" />
			<stop offset="90%" stop-color="white" stop-opacity="0.3" />
			<stop offset="100%" stop-color="white" stop-opacity="0.1" />
		`;
		defs.appendChild(metallicGradient);

		// Clone shape path so the texture is applied to the whole shape
		const clonedPath = shapePath.cloneNode(true) as SVGPathElement;
		clonedPath.removeAttribute("id");
		clonedPath.setAttribute("stroke", "none");
		clonedPath.setAttribute("fill", 'url(#metallicGradient)');
		shapePath.after(clonedPath);
	}

	// Add gloss texture
	if(sideAppearance === "gloss") {
		defs.innerHTML = `
			<linearGradient id="glossTextureGradient" gradientTransform="rotate(-100, 0.5, 0.5)" x1="0" y1="0" x2="0" y2="1">
				<stop stop-color="white" stop-opacity="0" />
				<stop offset="59.999%" stop-color="white" stop-opacity="0" />
				<stop offset="60%" stop-color="white" stop-opacity="30%" />
				<stop offset="90%" stop-color="white" stop-opacity="0" />
			</linearGradient>
			<linearGradient id="glossTextureFade" y2="1" x2="0">
				<stop offset="0" stop-color="white" stop-opacity="1" />
				<stop offset="0.7" stop-color="white" stop-opacity="0" />
			</linearGradient>
			<mask id="glossTextureMask" maskContentUnits="objectBoundingBox">
				<rect width="1" height="1" fill="url(#glossTextureFade)" />
			</mask>
		`;

		// Clone shape path so the texture is applied to the whole shape
		const clonedPath = shapePath.cloneNode(true) as SVGPathElement;
		clonedPath.removeAttribute("id");
		clonedPath.setAttribute("stroke", "none");
		clonedPath.setAttribute("fill", 'url(#glossTextureGradient)');
		clonedPath.setAttribute("mask", 'url(#glossTextureMask)');
		shapePath.after(clonedPath);
	}

	if(sideAppearance === "transparent") {
		defs.innerHTML = `
			<linearGradient id="transparentTextureGradient" gradientTransform="rotate(-100, 0.5, 0.5)" x1="0" y1="0" x2="0" y2="1">
				<stop stop-color="white" stop-opacity="0" />
				<stop offset="59.999%" stop-color="white" stop-opacity="0" />
				<stop offset="60%" stop-color="black" stop-opacity="15%" />
				<stop offset="90%" stop-color="black" stop-opacity="0" />
			</linearGradient>
			<linearGradient id="transparentTextureFade" y2="1" x2="0">
				<stop offset="0" stop-color="white" stop-opacity="1" />
				<stop offset="0.7" stop-color="white" stop-opacity="0" />
			</linearGradient>
			<mask id="transparentTextureMask" maskContentUnits="objectBoundingBox">
				<rect width="1" height="1" fill="url(#transparentTextureFade)" />
			</mask>
		`;

		// Clone shape path so the texture is applied to the whole shape
		const clonedPath = shapePath.cloneNode(true) as SVGPathElement;
		clonedPath.removeAttribute("id");
		clonedPath.setAttribute("stroke", "none");
		clonedPath.setAttribute("fill", 'url(#transparentTextureGradient)');
		clonedPath.setAttribute("mask", 'url(#transparentTextureMask)');
		shapePath.after(clonedPath);
	}

	// Add brushed or copper texture
	if(sideAppearance === "copper" || sideAppearance === "brushed" || sideAppearance === "black") {
		sideColor = sideAppearance === 'copper' ? '#d1793b' : sideColor;

		const brushedTextureHighlightGradient = document.createElementNS(
			"http://www.w3.org/2000/svg",
			"linearGradient"
		);
		brushedTextureHighlightGradient.setAttribute("id", `brushedTextureHighlight-${sideColor}`);
		brushedTextureHighlightGradient.setAttribute("x1", "0");
		brushedTextureHighlightGradient.setAttribute("x2", "0");
		brushedTextureHighlightGradient.setAttribute("y1", "0");
		brushedTextureHighlightGradient.setAttribute("y2", "1");
		brushedTextureHighlightGradient.setAttribute("gradientTransform", "rotate(80, 0.5, 0.5)");
		brushedTextureHighlightGradient.innerHTML = `
			<stop offset="0%" stop-color="black" stop-opacity="0.3" />
			<stop offset="10%" stop-color="black" stop-opacity="0" />
			<stop offset="30%" stop-color="black" stop-opacity="0" />
			<stop offset="40%" stop-color="white" stop-opacity="0.15" />
			<stop offset="50%" stop-color="white" stop-opacity="0.4" />
			<stop offset="60%" stop-color="white" stop-opacity="0.15" />
			<stop offset="70%" stop-color="black" stop-opacity="0" />
			<stop offset="90%" stop-color="black" stop-opacity="0" />
			<stop offset="100%" stop-color="black" stop-opacity="0.3" />
		`;
		defs.appendChild(brushedTextureHighlightGradient);

		const brushedTextureBrushingFilter = document.createElementNS(
			"http://www.w3.org/2000/svg",
			"filter"
		);

		brushedTextureBrushingFilter.setAttribute("id", `brushedTextureBrushing-${sideColor}`);
		brushedTextureBrushingFilter.innerHTML = `
			<feTurbulence type="fractalNoise" baseFrequency=".0006 10" numOctaves="5" />
			<feColorMatrix type="matrix" values="1   0   0   0   0
				1   0   0   0   0
				1   0   0   0   0
				0   0   0   1   0 " />
			<feColorMatrix
				color-interpolation-filters="sRGB"
				type="matrix"
				values="${getColorMatrixValues(sideColor, sideAppearance)}"
			/>
			<feComposite operator="in" in2="SourceGraphic" />
		`;

		svgElement.prepend(brushedTextureBrushingFilter);

		const brushedTextureNoiseFilter = document.createElementNS(
			"http://www.w3.org/2000/svg",
			"filter"
		);

		brushedTextureNoiseFilter.setAttribute("id", `brushedTextureNoise-${sideColor}`);
		brushedTextureNoiseFilter.innerHTML = `
			<feTurbulence type="fractalNoise" baseFrequency="10" numOctaves="3" stitchTiles="stitch" />
			<feComposite operator="in" in2="SourceGraphic" />
		`;

		svgElement.prepend(brushedTextureNoiseFilter);

		// Clone shape path so the textures are applied to the whole shape
		const brushedTextureHighlight = shapePath.cloneNode(true) as SVGPathElement;
		brushedTextureHighlight.removeAttribute("id");
		brushedTextureHighlight.setAttribute("stroke", "none");
		brushedTextureHighlight.setAttribute("fill", `url(#brushedTextureHighlight-${sideColor})`);
		shapePath.after(brushedTextureHighlight);

		const brushedTextureBrushing = shapePath.cloneNode(true) as SVGPathElement;
		brushedTextureBrushing.removeAttribute("id");
		brushedTextureBrushing.setAttribute("stroke", "none");
		brushedTextureBrushing.setAttribute("fill", sideColor);
		brushedTextureBrushing.setAttribute("filter", `url(#brushedTextureBrushing-${sideColor})`);
		shapePath.after(brushedTextureBrushing);

		const brushedTextureNoise = shapePath.cloneNode(true) as SVGPathElement;
		brushedTextureNoise.removeAttribute("id");
		brushedTextureNoise.setAttribute("stroke", "none");
		brushedTextureNoise.setAttribute("fill", sideColor);
		brushedTextureNoise.setAttribute("filter", `url(#brushedTextureNoise-${sideColor})`);
		brushedTextureNoise.setAttribute("opacity", '0.2');
		shapePath.after(brushedTextureNoise);
	}

	if(sideAppearance === 'custom_texture') {
		const {url, width, height} = window.measurementSelectorVariables.settings.customTexture;
		const image = document.createElementNS("http://www.w3.org/2000/svg", "image");
		image.setAttribute("preserveAspectRatio", "xMidYMid slice");
		image.setAttribute("x", "0");
		image.setAttribute("y", "0");
		image.setAttribute("width", `${width}`);
		image.setAttribute("height", `${height}`);

		const pattern = document.createElementNS(
			"http://www.w3.org/2000/svg",
			"pattern"
		);

		// set image options
		image.setAttribute("href", url);

		// set pattern options
		pattern.setAttribute("id", "customBitmapTexture");
		pattern.setAttribute("patternUnits", "objectBoundingBox");
		pattern.setAttribute("patternContentUnits", "userSpaceOnUse");
		pattern.setAttribute("x", "0");
		pattern.setAttribute("y", "0");
		pattern.setAttribute("width", "1");
		pattern.setAttribute("height", "1");
		pattern.appendChild(image);

		// set defs options
		defs.appendChild(pattern);

		// Clone shape path so the texture is applied to the whole shape
		const clonedPath = shapePath.cloneNode(true) as SVGPathElement;
		clonedPath.removeAttribute("id");
		clonedPath.setAttribute("stroke", "none");
		clonedPath.setAttribute("fill", `url(#customBitmapTexture)`);
		shapePath.after(clonedPath);
	}

	svgElement.prepend(defs);

	// Change shape color based of color or black appearance
	if(sideAppearance === "black") {
		shapePath.setAttribute("fill", "#333");
	} else {
		shapePath.setAttribute("fill", sideColor);
	}

	const serializer = new XMLSerializer();
	return serializer.serializeToString(svgElement);
};

export const getColorMatrixValues = (
	sideColor: string,
	sideAppearance: string
): string => {
	const color = sideAppearance === "copper" ? "#d1793b" : sideColor;
	const hex = color.replace("#", "");
	const r = (parseInt(hex.substring(0, 2), 16) / 255).toFixed(2);
	const g = (parseInt(hex.substring(2, 4), 16) / 255).toFixed(2);
	const b = (parseInt(hex.substring(4, 6), 16) / 255).toFixed(2);

	return [r, 0, 0, 0, 0, 0, g, 0, 0, 0, 0, 0, b, 0, 0, 0, 0, 0, 1, 0].join(" ");
};

/**
 * Use to reorder the SVG,
 * some paths are needed to rendered before others.
 */
export const reorderSVG = (svgString: string): string => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(svgString, "image/svg+xml");
	const svgElement = doc.documentElement;
	const dimensionLines = svgElement.querySelector("#dimension-lines");
	const captions = svgElement.querySelector("#captions");

	if(dimensionLines === null || captions === null) {
		return svgString;
	}

	// Place the dimension lines just before the captions
	// to ensure they are rendered on top of all the other elements
	captions.parentNode.insertBefore(dimensionLines, captions);

	const serializer = new XMLSerializer();
	return serializer.serializeToString(svgElement);
};

export const addStyleToSVG = (
	svgString: string
):string => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(svgString, "image/svg+xml");
	const svgElement = doc.documentElement;

	const activeCutout = svgElement.querySelector("#active-cutout");
	if(activeCutout) {
		activeCutout.setAttribute("stroke", "#7ed321");
		activeCutout.setAttribute("fill", "#f0faff");
	}

	const activeDrillhole = svgElement.querySelector("#active-drillhole");
	if(activeDrillhole) {
		activeDrillhole.setAttribute("stroke", "#7ed321");
		activeDrillhole.setAttribute("fill", "#f0faff");
	}

	const hiddenRectangle = svgElement.querySelector("#hidden-rectangle");
	if(hiddenRectangle) {
		hiddenRectangle.setAttribute("fill", "none");
		hiddenRectangle.setAttribute("stroke", "none");
	}

	const serializer = new XMLSerializer();
	return serializer.serializeToString(svgElement);
};
